import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Upload, Plus, X, Clock, FileText, CheckCircle2, AlertTriangle, Loader2, Download, MapPin, PenLine } from "lucide-react";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const MATERIALS = ["cement", "tmt_bars", "structural_steel", "binding_wire", "plates_sheets", "aggregate", "ready_mix_concrete"];

const STATUS_META = {
  pending_confirmation: { label: "Awaiting Vinci confirmation", tone: "text-[#D8B23E] border-[#D8B23E]/40" },
  active:               { label: "Protected · Live",             tone: "text-[#3DBB84] border-[#3DBB84]/40" },
  expiring_soon:        { label: "Expiring soon",                 tone: "text-[#E5484D] border-[#E5484D]/40" },
  delivered:            { label: "Delivered · reconciliation due",tone: "text-[#2FE4C6] border-[#2FE4C6]/40" },
  reconciled:           { label: "Reconciled · awaiting payout",  tone: "text-[#F5F5F0] border-[#F5F5F0]/40" },
  paid:                 { label: "Paid",                          tone: "text-[#D8B23E] border-[#D8B23E]/40" },
  expired:              { label: "Expired",                       tone: "text-[#8A8A82] border-[#8A8A82]/40" },
  cancelled:            { label: "Cancelled",                     tone: "text-[#8A8A82] border-[#8A8A82]/40" },
};

export function DealProtectionDesk({ api }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [openRegister, setOpenRegister] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/deals/mine");
      setDeals(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load deals"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="deal-protection-desk" className="space-y-6">
      <div className="flex items-start gap-4 border-b border-white/10 pb-6">
        <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center shrink-0">
          <Shield size={20}/>
        </div>
        <div className="flex-1">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Deal Protection Desk</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Register indirect deals · lock the payout.</h2>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            Every prospective buyer you introduce gets a 180-day protection clock, an auto-generated NCND signature request, and a proof-of-involvement vault. When the material lands on site, Vinci reconciles your commission automatically.
          </p>
        </div>
        <button data-testid="register-deal-btn" onClick={() => setOpenRegister(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#D8B23E] text-black hover:bg-[#c5a233] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]">
          <Plus size={12}/> Register deal
        </button>
      </div>

      {loading ? (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-8 text-center font-body text-sm text-[#8A8A82]">
          <Loader2 size={16} className="inline animate-spin mr-2"/> Loading deals…
        </div>
      ) : deals.length === 0 ? (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-8 text-center">
          <div className="font-body text-sm text-[#8A8A82]">No deals registered yet.</div>
          <button onClick={() => setOpenRegister(true)}
                  className="mt-3 inline-flex items-center gap-1.5 border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] rounded-sm">
            <Plus size={12}/> Register your first deal
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {deals.map(d => (
            <DealCard key={d.id} deal={d} onOpen={() => setActive(d)}/>
          ))}
        </div>
      )}

      <AnimatePresence>
        {openRegister && <RegisterDealModal api={api}
                                            onClose={() => setOpenRegister(false)}
                                            onDone={() => { setOpenRegister(false); load(); }}/>}
        {active && <DealDrawer api={api} deal={active}
                                onClose={() => setActive(null)}
                                onUpdated={load}/>}
      </AnimatePresence>
    </div>
  );
}


function DealCard({ deal, onOpen }) {
  const meta = STATUS_META[deal.status] || { label: deal.status, tone: "text-[#8A8A82] border-white/20" };
  const daysLeft = deal.protection_days_left;
  return (
    <button onClick={onOpen} data-testid={`deal-card-${deal.id}`}
            className="text-left border border-white/10 rounded-sm bg-[#0a0a0a] hover:border-[#D8B23E]/40 transition-colors p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-mono text-[13px] text-[#D8B23E]">{deal.deal_ref}</div>
          <div className="font-serif text-lg text-[#F5F5F0]">{deal.buyer_name}</div>
          <div className="font-body text-[11px] text-[#8A8A82]">
            {deal.project} · <MapPin size={10} className="inline"/> {deal.site_address?.slice(0, 60)}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em] ${meta.tone}`}>
          {meta.label}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <div><div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">Est. value</div>
          <div className="font-body text-sm text-[#F5F5F0]">{inr(deal.est_value)}</div></div>
        <div><div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">Materials</div>
          <div className="font-body text-sm text-[#F5F5F0] capitalize">{(deal.material_scope||[]).join(", ").replace(/_/g," ")}</div></div>
        <div><div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">NCND</div>
          <div className="font-body text-sm text-[#F5F5F0] capitalize">{deal.ncnd_status?.replace(/_/g," ")}</div></div>
        <div><div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">Protection</div>
          <div className={`font-body text-sm ${deal.protection_expiring_soon ? "text-[#E5484D]" : "text-[#3DBB84]"}`}>
            {daysLeft === null ? "Awaiting confirm" : daysLeft <= 0 ? "Expired" : `${daysLeft} days left`}
          </div></div>
      </div>
    </button>
  );
}


function RegisterDealModal({ api, onClose, onDone }) {
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "", buyer_phone: "",
    project: "", site_address: "", est_value: "", est_tonnage: "",
    material_scope: [], remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleMat = (m) => setForm(p => ({
    ...p, material_scope: p.material_scope.includes(m)
      ? p.material_scope.filter(x => x !== m) : [...p.material_scope, m]
  }));

  const ready = form.buyer_name.trim() && form.buyer_email.includes("@") && form.buyer_phone.trim()
                && form.project.trim() && form.site_address.trim() && Number(form.est_value) > 0
                && form.material_scope.length > 0;

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      await api.post("/deals", {
        ...form, est_value: Number(form.est_value),
        est_tonnage: Number(form.est_tonnage || 0),
      });
      toast.success("Deal registered · NCND dispatched for signature");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Registration failed"); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="bg-[#050506] w-full max-w-3xl border border-[#D8B23E]/40 rounded-sm overflow-hidden"
        data-testid="register-deal-modal">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Register Deal</div>
            <div className="font-serif text-xl text-[#F5F5F0] mt-1">Log a prospective buyer</div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-1">
              180-day protection clock arms once Vinci Ops confirms the supplier.
            </div>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]" data-testid="register-close">
            <X size={20}/>
          </button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-3">
          <Input testid="dr-buyer" label="Buyer name *" value={form.buyer_name} onChange={v => set("buyer_name", v)}/>
          <Input testid="dr-email" label="Buyer email *" value={form.buyer_email} onChange={v => set("buyer_email", v)} placeholder="prospect@company.com"/>
          <Input testid="dr-phone" label="Buyer phone *" value={form.buyer_phone} onChange={v => set("buyer_phone", v)}/>
          <Input testid="dr-project" label="Project / Site name *" value={form.project} onChange={v => set("project", v)}/>
          <div className="sm:col-span-2">
            <Input testid="dr-address" label="Site address *" value={form.site_address} onChange={v => set("site_address", v)}/>
          </div>
          <Input testid="dr-value" label="Estimated value (INR) *" type="number" value={form.est_value} onChange={v => set("est_value", v)}/>
          <Input testid="dr-tonnage" label="Estimated tonnage / units" type="number" value={form.est_tonnage} onChange={v => set("est_tonnage", v)}/>
          <div className="sm:col-span-2">
            <div className="font-body text-[11px] text-[#8A8A82] mb-1">Material scope *</div>
            <div className="flex flex-wrap gap-2">
              {MATERIALS.map(m => (
                <button key={m} onClick={() => toggleMat(m)} data-testid={`dr-mat-${m}`}
                        className={`font-grotesk text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm border transition-colors ${
                          form.material_scope.includes(m) ? "bg-[#D8B23E] text-black border-[#D8B23E]" : "border-white/15 text-[#8A8A82] hover:text-[#F5F5F0]"
                        }`}>
                  {m.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="font-body text-[11px] text-[#8A8A82] mb-1">Remarks (optional)</div>
            <textarea data-testid="dr-remarks" rows={2} value={form.remarks} onChange={e => set("remarks", e.target.value)}
                      className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
          </div>
        </div>
        <div className="border-t border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="font-body text-[11px] text-[#8A8A82]">
            Submitting fires the NCND agreement to your email via Zoho Sign.
          </div>
          <button data-testid="register-submit" onClick={submit} disabled={!ready || busy}
                  className={`font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-3 rounded-sm inline-flex items-center gap-2 ${
                    ready && !busy ? "bg-[#D8B23E] hover:bg-[#c5a233] text-black" : "bg-white/5 text-[#8A8A82] cursor-not-allowed"
                  }`}>
            {busy ? <Loader2 size={12} className="animate-spin"/> : <PenLine size={12}/>}
            Register & send NCND
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


function Input({ testid, label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      <div className="font-body text-[11px] text-[#8A8A82] mb-1">{label}</div>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder}
             onChange={e => onChange(e.target.value)}
             className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
    </div>
  );
}


function DealDrawer({ api, deal, onClose, onUpdated }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [entry, setEntry] = useState({ kind: "site_visit", note: "", file: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/deals/${deal.id}/timeline`);
      setTimeline(r.data || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [api, deal.id]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!entry.file) { toast.error("Attach a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", entry.kind);
      fd.append("note", entry.note);
      fd.append("file", entry.file);
      await api.post(`/deals/${deal.id}/timeline`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Proof archived");
      setEntry({ kind: "site_visit", note: "", file: null });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const openNcnd = async () => {
    try {
      const r = await api.get(`/deals/${deal.id}/ncnd/embed`);
      if (r.data.embed_url) window.open(r.data.embed_url, "_blank");
      else toast.error("NCND link not ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open NCND"); }
  };

  const download = async (t) => {
    try {
      const r = await api.get(`/deals/${deal.id}/timeline/${t.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not download"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-stretch justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
        className="bg-[#050506] w-full max-w-2xl border-l border-[#D8B23E]/30 h-full overflow-y-auto"
        data-testid="deal-drawer">
        <div className="sticky top-0 z-10 bg-[#050506]/95 backdrop-blur flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Deal Timeline</div>
            <div className="font-serif text-xl text-[#F5F5F0] font-mono">{deal.deal_ref}</div>
            <div className="font-body text-[11px] text-[#8A8A82]">{deal.buyer_name} · {deal.project}</div>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]" data-testid="drawer-close"><X size={20}/></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Protection banner */}
          <div className={`border rounded-sm p-4 ${deal.protection_expiring_soon ? "border-[#E5484D]/40 bg-[#E5484D]/[0.06]" : "border-[#3DBB84]/40 bg-[#3DBB84]/[0.05]"}`}>
            <div className="flex items-center gap-2">
              <Clock size={14} className={deal.protection_expiring_soon ? "text-[#E5484D]" : "text-[#3DBB84]"}/>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#F5F5F0]">
                {deal.protection_days_left === null ? "Awaiting supplier confirmation" :
                 deal.protection_days_left <= 0 ? "Protection expired" :
                 `Protection · ${deal.protection_days_left} of ${deal.protection_days || 180} days remaining`}
              </div>
            </div>
          </div>

          {/* NCND card */}
          <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E]">NCND Agreement</div>
                <div className="font-body text-sm text-[#F5F5F0] mt-1 capitalize">
                  {deal.ncnd_provider?.replace("_"," ") || "manual"} · {deal.ncnd_status?.replace(/_/g," ") || "pending"}
                </div>
              </div>
              <button onClick={openNcnd} data-testid="open-ncnd"
                      className="inline-flex items-center gap-1.5 border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 rounded-sm px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]">
                <PenLine size={11}/> Open signing page
              </button>
            </div>
          </div>

          {/* Add proof */}
          <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-3">Add proof of involvement</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="font-body text-[11px] text-[#8A8A82] mb-1">Kind</div>
                <select data-testid="tl-kind" value={entry.kind}
                        onChange={e => setEntry(p => ({...p, kind: e.target.value}))}
                        className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0]">
                  <option value="site_visit">Site visit</option>
                  <option value="meeting_minutes">Meeting minutes</option>
                  <option value="blueprint">Blueprint / specs</option>
                  <option value="pipeline">Pipeline update</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <div className="font-body text-[11px] text-[#8A8A82] mb-1">Attach file (PDF/PNG/JPEG ≤ 10 MB)</div>
                <label className="cursor-pointer inline-flex items-center gap-2 rounded-sm border border-white/15 hover:border-[#D8B23E] px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#F5F5F0] w-full">
                  <Upload size={11}/> {entry.file ? entry.file.name.slice(0, 24) : "Choose file"}
                  <input data-testid="tl-file" type="file" accept="application/pdf,image/*"
                         onChange={e => setEntry(p => ({...p, file: e.target.files?.[0] || null}))}
                         className="hidden"/>
                </label>
              </div>
              <div className="sm:col-span-2">
                <div className="font-body text-[11px] text-[#8A8A82] mb-1">Note</div>
                <textarea data-testid="tl-note" rows={2} value={entry.note}
                          onChange={e => setEntry(p => ({...p, note: e.target.value}))}
                          className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
              </div>
            </div>
            <button data-testid="tl-upload" onClick={upload} disabled={uploading || !entry.file}
                    className={`mt-3 inline-flex items-center gap-2 rounded-sm px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] ${
                      entry.file && !uploading ? "bg-[#D8B23E] text-black hover:bg-[#c5a233]" : "bg-white/5 text-[#8A8A82] cursor-not-allowed"
                    }`}>
              {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Archive proof
            </button>
          </div>

          {/* Timeline */}
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Proof timeline</div>
            {loading ? (
              <div className="font-body text-sm text-[#8A8A82]">Loading…</div>
            ) : timeline.length === 0 ? (
              <div className="font-body text-sm text-[#8A8A82]">No proofs archived yet.</div>
            ) : (
              <div className="space-y-2" data-testid="timeline-list">
                {timeline.map(t => (
                  <div key={t.id} data-testid={`timeline-row-${t.id}`}
                       className="border border-white/10 rounded-sm bg-black/30 p-3 flex items-start justify-between gap-4">
                    <div>
                      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E]">{t.kind.replace(/_/g," ")}</div>
                      <div className="font-body text-sm text-[#F5F5F0] mt-0.5">{t.note || "—"}</div>
                      <div className="font-body text-[10px] text-[#8A8A82] mt-1">
                        {t.filename} · {new Date(t.at).toLocaleString("en-IN", {day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit"})}
                      </div>
                    </div>
                    <button onClick={() => download(t)} data-testid={`tl-dl-${t.id}`}
                            className="text-[#D8B23E] hover:text-[#c5a233] shrink-0"><Download size={14}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
